/**
 * CaseStatusManager コンポーネント
 *
 * 管理者・配車担当者向けのステータス変更コンポーネント
 *
 * **通常モード（デフォルト）**: 線形フロー制約を強制
 * - provisional → confirmed → assigned → picked_up → in_transit → delivered → reported → completed
 * - ステップスキップ・巻き戻しは不可
 *
 * **特別操作モード**: ステータスを自由に変更可能
 * - 例外対応・システムエラー復旧時に使用
 * - 変更理由の入力が必須
 */

import React, { useState, useCallback } from 'react';
import { Case } from '@/types';
import { SupabaseServiceFactory } from '@/services/supabase';
import CaseStatusHistory from '@/components/ui/CaseStatusHistory';
import logger from '@/utils/logger';

interface CaseStatusManagerProps {
  /** 案件 ID */
  caseId: string;
  /** 現在のステータス */
  currentStatus: Case['status'] | null;
  /** ステータス更新時のコールバック */
  onStatusUpdated?: (newStatus: Case['status']) => void;
  /** エラー発生時のコールバック */
  onError?: (error: Error) => void;
  /** コンポーネント無効化 */
  disabled?: boolean;
  /** 追加クラス名 */
  className?: string;
}

interface StatusButtonConfig {
  status: Case['status'];
  label: string;
  icon: string;
}

type ButtonState = 'enabled' | 'disabled' | 'current';

/**
 * ボタンの状態に応じた色クラスを取得
 */
const getButtonColorClasses = (state: ButtonState, isCurrent: boolean): string => {
  if (isCurrent) {
    return 'bg-blue-600 text-white dark:bg-blue-700';
  }

  switch (state) {
    case 'enabled':
      return 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 cursor-pointer';
    case 'disabled':
      return 'bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-500 opacity-50 cursor-not-allowed';
    default:
      return '';
  }
};

/**
 * 現在のステータスに基づいてボタンの状態を判定
 */
const getButtonState = (
  targetStatus: Case['status'],
  currentStatus: Case['status'] | null,
  isSpecialMode: boolean
): ButtonState => {
  if (!currentStatus) return 'disabled';

  // ステータス順序定義
  const statusOrder: Case['status'][] = [
    'provisional',
    'confirmed',
    'assigned',
    'picked_up',
    'in_transit',
    'delivered',
    'reported',
    'completed',
  ];

  const currentIndex = statusOrder.indexOf(currentStatus);
  const targetIndex = statusOrder.indexOf(targetStatus);

  // 特別操作モード: すべてのステータスが有効
  if (isSpecialMode) {
    return 'enabled';
  }

  // 通常モード: 次のステータスのみ有効
  if (targetIndex === currentIndex + 1) {
    return 'enabled';
  }

  return 'disabled';
};

/**
 * CaseStatusManager コンポーネント
 *
 * **通常モード**: 線形フロー制約により、次のステータスのみ選択可能
 * **特別操作モード**: すべてのステータス変更が可能（例外対応用）
 */
export const CaseStatusManager: React.FC<CaseStatusManagerProps> = ({
  caseId,
  currentStatus,
  onStatusUpdated,
  onError,
  disabled = false,
  className = '',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<Case['status'] | null>(null);
  const [notesText, setNotesText] = useState('');
  const [showNotesInput, setShowNotesInput] = useState(false);
  const [isSpecialOperationMode, setIsSpecialOperationMode] = useState(false);

  // 8つのステータスボタン設定
  const statusButtons: StatusButtonConfig[] = [
    { status: 'provisional', label: '仮受', icon: '📝' },
    { status: 'confirmed', label: '本受・未配車', icon: '✅' },
    { status: 'assigned', label: '配車済', icon: '🚗' },
    { status: 'picked_up', label: '引取済', icon: '🔄' },
    { status: 'in_transit', label: '輸送中', icon: '🛣️' },
    { status: 'delivered', label: '搬入済', icon: '📍' },
    { status: 'reported', label: '報告済', icon: '📋' },
    { status: 'completed', label: '完了', icon: '🎉' },
  ];

  // ステータス更新ハンドラー
  const handleStatusUpdate = useCallback(
    async (newStatus: Case['status']) => {
      try {
        setIsLoading(true);

        // 特別操作モード時は変更理由入力必須
        if (isSpecialOperationMode && !notesText.trim()) {
          const err = new Error('特別操作モードでは変更理由の入力が必須です');
          onError?.(err);
          setIsLoading(false);
          return;
        }

        const caseService = SupabaseServiceFactory.getCaseService();
        const updatedCase = await caseService.updateCaseStatus(
          caseId,
          newStatus,
          notesText || undefined
        );

        setNotesText('');
        setShowNotesInput(false);
        setSelectedStatus(null);

        onStatusUpdated?.(updatedCase.status as any);
        logger.info(`✅ 案件 ${caseId} のステータスを ${newStatus} に更新`);
      } catch (error) {
        const err = error instanceof Error ? error : new Error('ステータス更新に失敗しました');
        console.error('❌ ステータス更新エラー:', err);
        onError?.(err);
        logger.error('❌ ステータス更新エラー:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [caseId, notesText, onStatusUpdated, onError, isSpecialOperationMode]
  );

  const isButtonGroupDisabled = disabled || !currentStatus;

  return (
    <div className={`w-full space-y-6 ${className}`}>
      {/* 特別操作モード切り替え */}
      <div className='bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700'>
        <div className='flex items-center gap-3'>
          <input
            type='checkbox'
            id='special-operation-mode'
            checked={isSpecialOperationMode}
            onChange={e => setIsSpecialOperationMode(e.target.checked)}
            disabled={disabled || isLoading}
            className='w-5 h-5 rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500 dark:border-yellow-600 dark:text-yellow-500 dark:focus:ring-yellow-400 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
          />
          <label
            htmlFor='special-operation-mode'
            className='text-sm font-medium text-yellow-900 dark:text-yellow-200 cursor-pointer select-none'
          >
            ⚠️ 特別操作モード（ステータス自由変更）
          </label>
        </div>
        <p className='text-xs text-yellow-800 dark:text-yellow-300 mt-2 ml-8'>
          通常は次のステップのみ選択可能です。例外対応や復旧時にこのモードを有効にしてください。
        </p>
      </div>

      {/* 特別操作モード時の警告 */}
      {isSpecialOperationMode && (
        <div className='bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border-2 border-red-200 dark:border-red-700'>
          <p className='text-sm font-semibold text-red-700 dark:text-red-300'>
            ⚠️ 特別操作モードが有効です
          </p>
          <p className='text-sm text-red-600 dark:text-red-400 mt-1'>
            変更理由の入力が必須です。必ず正当な理由を記入してください。
          </p>
        </div>
      )}

      {/* ステータスボタングループ */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-lg p-4 border border-gray-200 dark:border-gray-700'>
        <h3 className='text-sm font-semibold text-gray-900 dark:text-white mb-4'>ステータス変更</h3>

        {/* モード表示 */}
        <div className='mb-4 p-3 bg-blue-50 dark:bg-blue-900/10 rounded border border-blue-200 dark:border-blue-700'>
          <p className='text-xs text-blue-700 dark:text-blue-300'>
            {isSpecialOperationMode ? (
              <>
                <strong>特別操作モード</strong>: すべてのステータスに自由に変更できます
              </>
            ) : (
              <>
                <strong>通常モード</strong>: 次のステップのみ選択可能です
              </>
            )}
          </p>
        </div>

        {/* ステータスボタングリッド */}
        <div className='gap-2 flex flex-wrap'>
          {statusButtons.map(button => {
            const isCurrentStatus = button.status === currentStatus;
            const buttonState = getButtonState(
              button.status,
              currentStatus,
              isSpecialOperationMode
            );
            const isButtonClickDisabled =
              isButtonGroupDisabled || buttonState === 'disabled' || isLoading;
            const buttonColorClasses = getButtonColorClasses(buttonState, isCurrentStatus);

            return (
              <button
                key={button.status}
                onClick={() => {
                  if (!isButtonClickDisabled) {
                    setSelectedStatus(button.status);
                    setShowNotesInput(true);
                  }
                }}
                disabled={isButtonClickDisabled}
                className={`px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${buttonColorClasses}`}
                aria-label={`${button.label}: ステータスを${button.label}に変更${isCurrentStatus ? '（現在のステータス）' : buttonState === 'enabled' ? '' : '（選択不可）'}`}
                title={
                  isCurrentStatus
                    ? '現在のステータス'
                    : buttonState === 'enabled'
                      ? '選択可能'
                      : '通常モードでは選択できません'
                }
              >
                <span className='mr-1'>{button.icon}</span>
                {button.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 変更理由入力エリア */}
      {showNotesInput && selectedStatus && (
        <div
          className={`rounded-lg p-4 space-y-3 border-2 ${
            isSpecialOperationMode
              ? 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-700'
              : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}
        >
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isSpecialOperationMode
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              {isSpecialOperationMode ? '変更理由（必須）' : '変更理由（オプション）'}
            </label>
            <textarea
              value={notesText}
              onChange={e => setNotesText(e.target.value)}
              placeholder={
                isSpecialOperationMode
                  ? '例：システムエラーからの復旧、例外対応など、正当な理由を記入してください'
                  : '備考を入力してください（例：予定より早く到着、途中で給油、など）'
              }
              disabled={isLoading}
              required={isSpecialOperationMode}
              className={`w-full px-3 py-2 text-base border rounded-lg dark:text-white resize-none focus:outline-none focus:ring-2 ${
                isSpecialOperationMode
                  ? 'border-red-300 dark:bg-red-900/20 dark:border-red-600 dark:placeholder-red-400 focus:ring-red-500 dark:focus:ring-red-400'
                  : 'border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 focus:ring-blue-500 dark:focus:ring-blue-400'
              }`}
              rows={3}
            />
          </div>

          <div className='flex gap-2'>
            <button
              onClick={() => handleStatusUpdate(selectedStatus)}
              disabled={isLoading}
              className='flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isLoading ? '処理中...' : '確定する'}
            </button>
            <button
              onClick={() => {
                setShowNotesInput(false);
                setSelectedStatus(null);
                setNotesText('');
              }}
              disabled={isLoading}
              className='flex-1 py-2 px-4 bg-gray-400 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* ステータス変更履歴 */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-lg p-4 border border-gray-200 dark:border-gray-700'>
        <h3 className='text-sm font-semibold text-gray-900 dark:text-white mb-4'>変更履歴</h3>
        <CaseStatusHistory caseId={caseId} />
      </div>
    </div>
  );
};

export default CaseStatusManager;
